// weekly_review.jsx — Claude's weekly check-in with suggestions you can apply.
//
// runWeeklyReview (ai.js) asks Claude Haiku 4.5 for a structured review of the
// last 7 days from the lifter's own data. Each suggestion is one of:
//   program_change  edits to the active program (dry-run validated before it is shown)
//   volume          a per-muscle set rebalance, also as program edits
//   deload          switch this week to a deload
// Nothing changes until the user taps Apply. One review is cached per week.

import React from 'react';
import { useApp } from './store.jsx';
import { LL, LLIcon } from './tokens.jsx';
import { aiEnabled, runWeeklyReview, openCoachWith, useAIStatus } from './ai.js';
import { applyProgramChanges } from './coach_tools.js';
import { loadCatalog, catalogToExercise } from './exercise_catalog.js';
import { weekStart } from './analytics.js';

const keyFor = (ws) => 'liftlog.weeklyReview.' + ws;
function loadReview(ws) { try { return JSON.parse(localStorage.getItem(keyFor(ws)) || 'null'); } catch { return null; } }
function saveReview(ws, r) { try { localStorage.setItem(keyFor(ws), JSON.stringify(r)); } catch { /* full */ } }

const opLabel = { update: 'Adjust', swap: 'Swap', add: 'Add', remove: 'Remove', set_tm: 'Training max' };
export function changeLine(c) {
  const bits = [c.sets && `${c.sets} sets`, (c.rep_min || c.rep_max) && `${c.rep_min ?? '?'}–${c.rep_max ?? '?'} reps`, c.weight_lb != null && `${c.weight_lb} lb`, c.tm_lb != null && `TM ${c.tm_lb} lb`].filter(Boolean);
  return { head: `${c.day}: ${c.op === 'swap' ? `${c.exercise} → ${c.new_exercise}` : c.exercise}`, tail: bits.join(', ') };
}

/**
 * The weekly review card. Renders nothing when AI is off or there is too little
 * history (3 completed workouts). `style` merges onto the outer card.
 */
export function WeeklyReviewCard({ style }) {
  const { state, actions, toast, setRoute } = useApp();
  useAIStatus();
  const ws = weekStart();
  const [review, setReview] = React.useState(() => loadReview(ws));
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const program = state.programs.find(p => p.id === state.activeProgramId) || null;
  const done = (state.sessions || []).filter(s => s.completedAt).length;
  if (!aiEnabled() || done < 3) return null;

  const update = (r) => { setReview(r); saveReview(ws, r); };
  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const catalog = await loadCatalog().catch(() => null);
      const r = await runWeeklyReview({ state, program, catalog });
      update({ ...r, status: {} });
    } catch (e) { setErr(e); }
    setBusy(false);
  };
  const setStatus = (id, st) => update({ ...review, status: { ...(review.status || {}), [id]: st } });
  const apply = async (sg) => {
    if (!program) { toast('No active program'); return; }
    if (sg.kind === 'deload') { actions.setDeloadWeek(program.id, true); setStatus(sg.id, 'applied'); return; }
    const catalog = await loadCatalog().catch(() => null);
    const res = applyProgramChanges(program, sg.changes, state.exercises, catalog);
    if (!res.applied.length) { toast('That change no longer fits your program'); setStatus(sg.id, 'dismissed'); return; }
    if (res.newExercises.length) actions.ensureExercises(res.newExercises.map(catalogToExercise));
    actions.updateProgram(program.id, { days: res.program.days, periodization: res.program.periodization, ...(res.program.tms ? { tms: res.program.tms } : {}) });
    setStatus(sg.id, 'applied');
    toast(`Program updated · ${res.applied.length} change${res.applied.length === 1 ? '' : 's'}`);
  };
  const ask = (sg) => openCoachWith({ kind: 'free', prompt: `About the weekly review suggestion "${sg.title}" (${sg.why}): ` }, setRoute);

  return (
    <div style={{ background: LL.surface, border: `1px solid ${LL.line}`, borderRadius: LL.r.md, padding: 14, ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <LLIcon.ai style={{ width: 16, height: 16, color: LL.accentText }} />
        <div style={kicker}>Weekly review</div>
        {review && !busy && <button onClick={run} style={{ marginLeft: 'auto', background: 'none', border: 'none', padding: '4px 0', color: LL.fgMute, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, cursor: 'pointer' }}>Refresh</button>}
      </div>

      {!review && (
        <>
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, lineHeight: 1.45, marginTop: 6 }}>
            How the last 7 days went, plus changes you can apply in one tap — from your own log.
          </div>
          <button onClick={run} disabled={busy} style={{ marginTop: 12, width: '100%', height: 46, borderRadius: LL.r.md, border: `1px solid ${LL.line2}`, background: LL.surface2, color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, cursor: busy ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {busy ? <><Dots /> Reviewing your week…</> : 'Review my week'}
          </button>
        </>
      )}

      {review && (
        <div style={{ opacity: busy ? 0.5 : 1, transition: `opacity 200ms ${LL.ease}` }}>
          <div style={{ fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, color: LL.fg, lineHeight: 1.15, marginTop: 8 }}>{review.headline}</div>
          {review.summary && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fg, lineHeight: 1.5, marginTop: 6 }}>{review.summary}</div>}
          {review.wins?.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
              {review.wins.map((w, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, lineHeight: 1.4 }}>
                  <LLIcon.check style={{ width: 16, height: 16, color: LL.accentText, flexShrink: 0, marginTop: 1 }} />{w}
                </div>
              ))}
            </div>
          )}
          {review.focus && (
            <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: LL.r.sm, background: LL.surface2, fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fg, lineHeight: 1.45 }}>
              <span style={{ ...kicker, display: 'block', marginBottom: 2 }}>Next week</span>{review.focus}
            </div>
          )}
          {review.suggestions?.map(sg => (
            <SuggestionCard key={sg.id} sg={sg} status={review.status?.[sg.id] || 'pending'} onApply={() => apply(sg)} onDismiss={() => setStatus(sg.id, 'dismissed')} onAsk={() => ask(sg)} />
          ))}
          {review.suggestions?.length === 0 && (
            <div style={{ marginTop: 10, fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgDim }}>No program changes needed this week.</div>
          )}
        </div>
      )}
      {err && (
        <div style={{ marginTop: 10, fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.danger, lineHeight: 1.4 }}>
          {err.message}
          {(err.code === 'quota' || err.code === 'relay_off') && (
            <button onClick={() => setRoute('settings/ai')} style={{ display: 'block', marginTop: 6, padding: 0, background: 'none', border: 'none', color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, cursor: 'pointer' }}>Use your own API key</button>
          )}
        </div>
      )}
    </div>
  );
}

function SuggestionCard({ sg, status, onApply, onDismiss, onAsk }) {
  const done = status !== 'pending';
  const tag = sg.kind === 'deload' ? 'Deload' : sg.kind === 'volume' ? `Volume${sg.muscle ? ` · ${sg.muscle}` : ''}` : 'Program change';
  return (
    <div style={{ marginTop: 12, borderRadius: LL.r.md, border: `1px solid ${done ? LL.line : `color-mix(in oklab, ${LL.accent} 55%, ${LL.line})`}`, background: LL.bg, overflow: 'hidden' }}>
      <div style={{ padding: '11px 12px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ ...kicker, color: LL.accentText }}>{tag}</span>
        {status === 'applied' && <span style={{ marginLeft: 'auto', fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 700, color: LL.accentText, display: 'flex', alignItems: 'center', gap: 4 }}><LLIcon.check style={{ width: 14, height: 14 }} />Applied</span>}
        {status === 'dismissed' && <span style={{ marginLeft: 'auto', fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgDim }}>Dismissed</span>}
      </div>
      <div style={{ padding: '4px 12px 10px' }}>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, color: LL.fg }}>{sg.title}</div>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginTop: 3, lineHeight: 1.45 }}>{sg.why}</div>
        {sg.kind === 'deload' && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgDim, marginTop: 6 }}>About 40 % fewer sets and 10 % lighter until Sunday.</div>}
        {sg.changes?.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {sg.changes.map((c, i) => {
              const l = changeLine(c);
              return (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  <span style={{ flexShrink: 0, padding: '2px 7px', borderRadius: LL.r.sm, background: LL.surface2, color: c.op === 'remove' ? LL.danger : LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700 }}>{opLabel[c.op] || c.op}</span>
                  <span style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fg, lineHeight: 1.4 }}>{l.head}{l.tail && <span style={{ color: LL.fgMute }}> · {l.tail}</span>}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {!done && (
        <div style={{ display: 'flex', gap: 8, padding: '0 12px 12px' }}>
          <button onClick={onApply} style={{ flex: 1, height: 42, borderRadius: LL.r.md, border: 'none', background: LL.accent, color: LL.accentInk, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, cursor: 'pointer' }}>{sg.kind === 'deload' ? 'Start deload' : 'Apply'}</button>
          <button onClick={onDismiss} style={{ height: 42, padding: '0 14px', borderRadius: LL.r.md, border: `1px solid ${LL.line2}`, background: 'transparent', color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, cursor: 'pointer' }}>Not now</button>
          <button onClick={onAsk} aria-label="Ask the coach about this" style={{ height: 42, width: 42, borderRadius: LL.r.md, border: `1px solid ${LL.line2}`, background: 'transparent', color: LL.accentText, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}><LLIcon.ai style={{ width: 18, height: 18 }} /></button>
        </div>
      )}
    </div>
  );
}

function Dots() {
  return (
    <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}>
      {[0, 1, 2].map(i => <span key={i} style={{ width: 5, height: 5, borderRadius: LL.r.xs, background: 'currentColor', opacity: 0.7, animation: `llPulse 1s ${i * 0.18}s infinite` }} />)}
    </span>
  );
}

const kicker = { fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1, color: LL.fgDim, textTransform: 'uppercase' };
